<template>
  <div class="marketing-shop">
    <!-- Header -->
    <!-- Header -->
    <div class="shop-header">
      <div class="d-flex align-center header-content">
        <div>
          <div class="shop-intro text-h6 font-weight-medium grey--text text--lighten-1">Bestelle Marketing-Material für dein Studio</div>
        </div>
        <v-spacer></v-spacer>

        <!-- Cart Icon -->
        <v-btn v-if="activeTab === 'shop'" icon large class="cart-btn" @click="openCart">
          <v-badge :content="cartItemCount" :value="cartItemCount > 0" color="error" overlap>
            <v-icon size="28" color="grey lighten-1">mdi-cart</v-icon>
          </v-badge>
        </v-btn>
      </div>

      <!-- Tabs -->
      <v-tabs v-model="activeTab" background-color="transparent" dark color="primary" slider-size="3" class="shop-tabs" show-arrows>
        <v-tab href="#shop" class="text-none">
          <v-icon left small>mdi-storefront</v-icon>
          Shop
        </v-tab>
        <v-tab href="#myOrders" class="text-none">
          <v-icon left small>mdi-package-variant-closed</v-icon>
          Meine Bestellungen
        </v-tab>
        <v-tab v-if="canManage" href="#allOrders" class="text-none">
          <v-icon left small>mdi-clipboard-list</v-icon>
          Alle Bestellungen
        </v-tab>
        <v-tab v-if="canManage" href="#productAdmin" class="text-none">
          <v-icon left small>mdi-cog</v-icon>
          Produkte verwalten
        </v-tab>
      </v-tabs>
    </div>

    <!-- Content -->
    <div class="shop-content">
      <v-tabs-items v-model="activeTab" class="transparent">
        <!-- ============ SHOP ============ -->
        <v-tab-item value="shop">
          <div style="max-width: 1400px; margin: 0 auto; padding: 24px 16px">
            <!-- Suche -->
            <v-text-field
              v-model="productSearch"
              prepend-inner-icon="mdi-magnify"
              placeholder="Produkt suchen..."
              outlined
              dense
              dark
              hide-details
              clearable
              class="mb-6"
              style="max-width: 500px" />

            <!-- Produkt-Grid -->
            <div v-if="loadingProducts" class="text-center pa-12">
              <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
            </div>

            <div v-else-if="filteredProducts.length === 0" class="text-center pa-12 grey--text">
              <v-icon size="64" color="grey darken-1">mdi-package-variant</v-icon>
              <div class="mt-3 subtitle-1">Keine Produkte gefunden</div>
            </div>

            <div v-else class="product-grid">
              <product-card
                v-for="product in filteredProducts"
                :key="product._id"
                :product="product"
                :cart-quantity="getCartQuantity(product._id)"
                @add-to-cart="addToCart"
                @open-detail="openProductDetail" />
            </div>
          </div>
        </v-tab-item>

        <!-- ============ MEINE BESTELLUNGEN ============ -->
        <v-tab-item value="myOrders">
          <div style="max-width: 1400px; margin: 0 auto; padding: 24px 16px">
            <my-orders-list :orders="myOrders" :loading="loadingMyOrders" @open-detail="openOrderDetail" />
          </div>
        </v-tab-item>

        <!-- ============ ALLE BESTELLUNGEN (MANAGE) ============ -->
        <v-tab-item v-if="canManage" value="allOrders">
          <div style="max-width: 1400px; margin: 0 auto; padding: 24px 16px">
            <admin-orders-list
              :orders="allOrders"
              :loading="loadingAllOrders"
              :studio-options="studioFilterOptions"
              :filter-studio.sync="filterStudio"
              :filter-status.sync="filterStatus"
              @reload="loadAllOrders"
              @open-detail="openOrderDetail" />
          </div>
        </v-tab-item>

        <!-- ============ PRODUKTE VERWALTEN ============ -->
        <v-tab-item v-if="canManage" value="productAdmin">
          <div style="max-width: 1400px; margin: 0 auto; padding: 24px 16px">
            <product-admin :studio-options="studioOptions" :products="adminProducts" :loading="loadingAdminProducts" @reload="reloadAllProducts" />
          </div>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <!-- Cart Drawer -->
    <cart-drawer
      v-model="cartOpen"
      :items="cart"
      :products="products"
      :studio-options="studioOptions"
      :default-studio-id="getSelectedStudioId"
      :submitting="submittingOrder"
      @update-quantity="updateCartQuantity"
      @remove-item="removeFromCart"
      @clear-cart="clearCart"
      @submit-order="submitOrder" />

    <!-- Product Detail Dialog -->
    <product-detail-dialog
      v-model="productDetailOpen"
      :product="currentProduct"
      :cart-quantity="currentProduct ? getCartQuantity(currentProduct._id) : 0"
      @add-to-cart="addToCart" />

    <!-- Order Detail Dialog -->
    <order-detail-dialog
      v-model="orderDetailOpen"
      :order="currentOrder"
      :can-manage="canManage"
      @reload="reloadCurrentOrder"
      @order-updated="handleOrderUpdated" />
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import { mapState, mapGetters } from "vuex"
import socketService from "@/socketService"

import ProductCard from "./components/ProductCard.vue"
import CartDrawer from "./components/CartDrawer.vue"
import ProductDetailDialog from "./components/ProductDetailDialog.vue"
import MyOrdersList from "./components/MyOrdersList.vue"
import AdminOrdersList from "./components/AdminOrdersList.vue"
import OrderDetailDialog from "./components/OrderDetailDialog.vue"
import ProductAdmin from "./components/ProductAdmin.vue"

const CART_STORAGE_KEY = "marketingShop_cart_v1"

export default {
  name: "MarketingShop",
  components: {
    ProductCard,
    CartDrawer,
    ProductDetailDialog,
    MyOrdersList,
    AdminOrdersList,
    OrderDetailDialog,
    ProductAdmin,
  },
  data() {
    return {
      userService,
      activeTab: "shop",

      // Produkte (öffentliche Liste)
      products: [],
      loadingProducts: false,
      productSearch: "",

      // Admin-Produkte (alle, auch inaktive)
      adminProducts: [],
      loadingAdminProducts: false,

      // Bestellungen
      myOrders: [],
      loadingMyOrders: false,

      allOrders: [],
      loadingAllOrders: false,
      filterStudio: "all",
      filterStatus: "all",

      // Cart
      cart: [],
      cartOpen: false,
      submittingOrder: false,

      // Dialogs
      currentProduct: null,
      productDetailOpen: false,

      currentOrder: null,
      orderDetailOpen: false,
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    ...mapGetters("studioStore", ["getSelectedStudioId"]),
    canManage() {
      return userService.hasRight("d_manageMarketingShop")
    },
    canFetch() {
      return userService.hasRight("d_fetchMarketingShop") || userService.hasRight("d_manageMarketingShop")
    },
    cartItemCount() {
      return this.cart.reduce((sum, i) => sum + i.quantity, 0)
    },
    studioOptions() {
      console.log(this.storeStudios, "HEY")
      return this.storeStudios.filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
    },
    studioFilterOptions() {
      return [{ _id: "all", name: "Alle Studios" }, ...this.studioOptions]
    },
    filteredProducts() {
      if (!this.productSearch || !this.productSearch.trim()) return this.products
      const q = this.productSearch.trim().toLowerCase()
      return this.products.filter((p) => (p.title || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q))
    },
  },
  watch: {
    activeTab(val) {
      if (val === "myOrders") this.loadMyOrders()
      else if (val === "allOrders") this.loadAllOrders()
      else if (val === "productAdmin") this.loadAdminProducts()
    },
    filterStudio() {
      this.loadAllOrders()
    },
    filterStatus() {
      this.loadAllOrders()
    },
    cart: {
      deep: true,
      handler() {
        this.persistCart()
      },
    },
  },
  methods: {
    // ===== Cart =====
    loadCartFromStorage() {
      try {
        const raw = localStorage.getItem(CART_STORAGE_KEY)
        if (!raw) return
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) this.cart = parsed
      } catch (e) {
        console.error("Cart-Storage konnte nicht gelesen werden:", e)
      }
    },
    persistCart() {
      try {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(this.cart))
      } catch (e) {
        console.error("Cart-Persist fehlgeschlagen:", e)
      }
    },
    getCartQuantity(productId) {
      const found = this.cart.find((i) => i.product === productId)
      return found ? found.quantity : 0
    },
    addToCart({ product, quantity }) {
      const existing = this.cart.find((i) => i.product === product._id)
      const newQty = (existing ? existing.quantity : 0) + quantity
      const max = product.maxQuantity || 100
      const final = Math.min(newQty, max)

      if (existing) {
        existing.quantity = final
      } else {
        this.cart.push({
          product: product._id,
          title: product.title,
          quantity: Math.max(final, product.minQuantity || 1),
          minQuantity: product.minQuantity || 1,
          maxQuantity: product.maxQuantity || 100,
        })
      }

      this.$toast && this.$toast.success(`${product.title} zum Warenkorb hinzugefügt`)
    },
    updateCartQuantity({ productId, quantity }) {
      const item = this.cart.find((i) => i.product === productId)
      if (!item) return
      const min = item.minQuantity || 1
      const max = item.maxQuantity || 100
      item.quantity = Math.max(min, Math.min(max, quantity))
    },
    removeFromCart(productId) {
      this.cart = this.cart.filter((i) => i.product !== productId)
    },
    clearCart() {
      this.cart = []
    },
    openCart() {
      this.cartOpen = true
    },

    // ===== Order Submit =====
    async submitOrder({ studio, note }) {
      if (this.cart.length === 0) return

      this.submittingOrder = true
      try {
        const payload = {
          studio,
          note: note || "",
          items: this.cart.map((i) => ({
            product: i.product,
            quantity: i.quantity,
          })),
        }

        await api.marketingShop.createOrder(payload)

        this.clearCart()
        this.cartOpen = false
        this.$toast && this.$toast.success("Bestellung erfolgreich abgeschickt!")
        this.activeTab = "myOrders"
      } catch (e) {
        console.error("Bestellung fehlgeschlagen:", e)
        const msg = e.response?.data?.error || "Fehler beim Absenden der Bestellung"
        this.$toast ? this.$toast.error(msg) : alert(msg)
      } finally {
        this.submittingOrder = false
      }
    },

    async reloadAllProducts() {
      this.loadAdminProducts()
      this.loadProducts()
    },

    // ===== Loaders =====
    async loadProducts() {
      this.loadingProducts = true
      try {
        this.products = await api.marketingShop.fetchProducts()
      } catch (e) {
        console.error("Fehler beim Laden der Produkte:", e)
      } finally {
        this.loadingProducts = false
      }
    },
    async loadAdminProducts() {
      if (!this.canManage) return
      this.loadingAdminProducts = true
      try {
        this.adminProducts = await api.marketingShop.fetchProducts({ all: true })
      } catch (e) {
        console.error("Fehler beim Laden:", e)
      } finally {
        this.loadingAdminProducts = false
      }
    },
    async loadMyOrders() {
      this.loadingMyOrders = true
      try {
        const params = this.canManage ? { mine: true } : {}
        this.myOrders = await api.marketingShop.fetchOrders(params)
      } catch (e) {
        console.error("Fehler beim Laden:", e)
      } finally {
        this.loadingMyOrders = false
      }
    },
    async loadAllOrders() {
      if (!this.canManage) return
      this.loadingAllOrders = true
      try {
        const params = {}
        if (this.filterStudio && this.filterStudio !== "all") params.studio = this.filterStudio
        if (this.filterStatus && this.filterStatus !== "all") params.status = this.filterStatus
        this.allOrders = await api.marketingShop.fetchOrders(params)
      } catch (e) {
        console.error("Fehler beim Laden:", e)
      } finally {
        this.loadingAllOrders = false
      }
    },

    // ===== Detail Dialogs =====
    openProductDetail(product) {
      this.currentProduct = product
      this.productDetailOpen = true
    },
    async openOrderDetail(order) {
      try {
        this.currentOrder = await api.marketingShop.fetchOrder(order._id)
        this.orderDetailOpen = true
      } catch (e) {
        console.error("Order konnte nicht geladen werden:", e)
      }
    },
    async reloadCurrentOrder() {
      if (!this.currentOrder) return
      try {
        this.currentOrder = await api.marketingShop.fetchOrder(this.currentOrder._id)
      } catch (e) {
        console.error(e)
      }
    },
    handleOrderUpdated() {
      // nach Status-Change/Comment Listen aktualisieren
      if (this.activeTab === "myOrders") this.loadMyOrders()
      if (this.activeTab === "allOrders") this.loadAllOrders()
    },

    // ===== Realtime =====
    setupSocketListeners() {
      // Handler binden oder direkt Methoden nutzen
      socketService.on("marketingOrder:new", this.handleNewOrder)
      socketService.on("marketingOrder:updated", this.handleOrderStatusUpdate)
      socketService.on("marketingOrder:commented", this.handleCommentUpdate)
    },

    teardownSocketListeners() {
      socketService.off("marketingOrder:new", this.handleNewOrder)
      socketService.off("marketingOrder:updated", this.handleOrderStatusUpdate)
      socketService.off("marketingOrder:commented", this.handleCommentUpdate)
    },

    handleNewOrder() {
      if (this.activeTab === "allOrders") this.loadAllOrders()
      this.$toast?.info("Eine neue Bestellung ist eingegangen!")
    },

    handleOrderStatusUpdate(payload) {
      if (this.activeTab === "myOrders") this.loadMyOrders()
      if (this.activeTab === "allOrders") this.loadAllOrders()

      // Falls der User gerade die Details dieser Bestellung offen hat
      if (this.currentOrder && this.currentOrder._id === payload.orderId) {
        this.reloadCurrentOrder()
      }
    },

    handleCommentUpdate(payload) {
      if (this.currentOrder && this.currentOrder._id === payload.orderId) {
        this.reloadCurrentOrder()
      }
    },
  },
  created() {
    this.loadCartFromStorage()
    this.loadProducts()
  },
  mounted() {
    this.setupSocketListeners()
  },
  beforeDestroy() {
    this.teardownSocketListeners()
  },
}
</script>

<style scoped>
.marketing-shop {
  min-height: 100vh;

  color: #fff;
}

.shop-header {
  /* Kein grüner Hintergrund mehr, stattdessen transparent für den globalen Dark Mode */
  background: transparent;
  /* Subtile Trennlinie nach unten, damit die Tabs einen schönen Abschluss haben */
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom: 24px;
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 16px 8px 16px;
}

.shop-intro {
  letter-spacing: 0.5px !important;
}

.shop-tabs {
  max-width: 1400px;
  margin: 0 auto;
}

.cart-btn {
  transition: transform 0.2s ease, color 0.2s ease;
}
.cart-btn:hover {
  transform: scale(1.1);
}
.cart-btn:hover .v-icon {
  /* Lässt das Icon beim Hovern aufleuchten */
  color: #fff !important;
}

.cart-btn {
  transition: transform 0.2s ease;
}
.cart-btn:hover {
  transform: scale(1.1);
}

.shop-content {
  padding-bottom: 80px;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

@media (max-width: 600px) {
  .shop-title {
    font-size: 22px;
  }
  .product-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
  }
}

.transparent {
  background: transparent !important;
}
</style>
